import {
  AREA_TYPE_LABELS,
  COMPANY_LABELS,
  type AreaDto,
  type AreaSourceDto,
  type DiscoveredSourceDto,
  type UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Globe, Plus, Save, Trash2, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFilter } from '../app/FilterProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { ProfileEditor, ResolvedPreview } from '../features/brand/BrandAdmin.js';
import { useUsers } from '../features/content/useContent.js';
import { api } from '../lib/api.js';
import { cn } from '../lib/cn.js';

/**
 * "Områder" — the working view of a product area for the people who run it.
 *
 * Everyone sees the areas they own or contribute to (an admin sees them all).
 * Description, målgruppe and tone of voice are editable by anyone who works in
 * the area; who works there is the owner's call (BESLUTNINGER.md, fase 3).
 */
export function AreasPage() {
  const { user } = useAuth();
  const { areas, isLoading } = useFilter();
  const isAdmin = user?.role === 'ADMIN';

  const mine = useMemo(
    () =>
      areas.filter(
        (a) => a.ownerUserId === user?.id || a.members?.some((m) => m.userId === user?.id),
      ),
    [areas, user?.id],
  );
  const others = useMemo(
    () => (isAdmin ? areas.filter((a) => !mine.some((m) => m.id === a.id)) : []),
    [areas, mine, isAdmin],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = areas.find((a) => a.id === selectedId) ?? mine[0] ?? others[0] ?? null;

  useEffect(() => {
    if (!selectedId && selected) setSelectedId(selected.id);
  }, [selectedId, selected]);

  if (isLoading) return <SkeletonPanel lines={8} />;

  if (mine.length === 0 && others.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader />
        <GlassPanel>
          <EmptyState
            title="Du er ikke knyttet til noe område ennå."
            description="Be en administrator, eller produktansvarlig for et område, om å legge deg til som medarbeider."
          />
        </GlassPanel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader />
      <div className="grid gap-3 lg:grid-cols-[240px_1fr]">
        <nav aria-label="Områder" className="space-y-3">
          <AreaGroup
            title="Mine områder"
            areas={mine}
            selectedId={selected?.id}
            onSelect={setSelectedId}
            currentUserId={user?.id}
          />
          {others.length > 0 && (
            <AreaGroup
              title="Andre områder"
              areas={others}
              selectedId={selected?.id}
              onSelect={setSelectedId}
              currentUserId={user?.id}
            />
          )}
        </nav>

        <div className="min-w-0">
          {selected ? (
            <AreaDetail key={selected.id} area={selected} />
          ) : (
            <GlassPanel>
              <EmptyState title="Velg et område" />
            </GlassPanel>
          )}
        </div>
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <header>
      <h1 className="font-display text-ink text-xl">Områder</h1>
      <p className="text-ink-soft mt-0.5 text-sm">
        Detaljene og tonen for områdene du jobber i. Dette former det Nova skriver.
      </p>
    </header>
  );
}

function AreaGroup({
  title,
  areas,
  selectedId,
  onSelect,
  currentUserId,
}: {
  title: string;
  areas: AreaDto[];
  selectedId?: string;
  onSelect: (id: string) => void;
  currentUserId?: string;
}) {
  if (areas.length === 0) return null;
  return (
    <div>
      <p className="label-micro mb-1.5 px-1">{title}</p>
      <div className="space-y-0.5">
        {areas.map((area) => {
          const role =
            area.ownerUserId === currentUserId
              ? 'Ansvarlig'
              : area.members?.some((m) => m.userId === currentUserId)
                ? 'Medarbeider'
                : null;
          return (
            <button
              key={area.id}
              type="button"
              onClick={() => onSelect(area.id)}
              aria-current={area.id === selectedId}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition',
                area.id === selectedId
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-soft hover:text-ink hover:bg-raised/60',
              )}
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: area.color }}
              />
              <span className="min-w-0 flex-1 truncate">{area.name}</span>
              {role && <span className="text-ink-soft/70 shrink-0 text-[10px]">{role}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AreaDetail({ area }: { area: AreaDto }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isOwner = area.ownerUserId === user?.id;
  const canManageMembers = isAdmin || isOwner;
  // Anyone who works in the area may tend its profile, tone and sources.
  const canEditArea = isAdmin || (area.members ?? []).some((m) => m.userId === user?.id);

  return (
    <div className="min-w-0 space-y-3">
      <GlassPanel>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-ink text-base">{area.name}</h2>
          <p className="text-ink-soft text-[11px]">
            {AREA_TYPE_LABELS[area.type]} · {COMPANY_LABELS[area.company]}
            {area.owner ? ` · Ansvarlig: ${area.owner.name}` : ''}
          </p>
        </div>
        <AreaProfileForm area={area} />
      </GlassPanel>

      <CoworkersPanel area={area} canManage={canManageMembers} />

      <SourcesPanel area={area} canEdit={canEditArea} />

      <ProfileEditor
        key={`area-${area.id}`}
        level={{ scope: 'AREA', areaId: area.id, label: `Tone for ${area.name}` }}
      />

      <ResolvedPreview areaId={area.id} />
    </div>
  );
}

/**
 * The sources the scout (Omverdensovervåkeren) watches for this area, plus the
 * switch that lets Nova look beyond the list. Editable by anyone who works here.
 */
function SourcesPanel({ area, canEdit }: { area: AreaDto; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const inputClass =
    'border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 h-9 w-full rounded-lg border px-3 text-sm';

  const { data: sources = [] } = useQuery({
    queryKey: ['area-sources', area.id],
    queryFn: () => api.get<AreaSourceDto[]>(`/areas/${area.id}/sources`),
  });
  const { data: discovered = [] } = useQuery({
    queryKey: ['area-discovered', area.id],
    queryFn: () =>
      api.get<DiscoveredSourceDto[]>(`/areas/${area.id}/discovered-sources?status=SUGGESTED`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['area-sources', area.id] });
    void queryClient.invalidateQueries({ queryKey: ['area-discovered', area.id] });
    void queryClient.invalidateQueries({ queryKey: ['areas'] });
  };

  const [form, setForm] = useState({ label: '', url: '', notes: '' });
  const add = useMutation({
    mutationFn: () =>
      api.post(`/areas/${area.id}/sources`, {
        label: form.label.trim(),
        url: form.url.trim(),
        notes: form.notes.trim() || null,
      }),
    onSuccess: () => {
      setForm({ label: '', url: '', notes: '' });
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/areas/${area.id}/sources/${id}`),
    onSuccess: invalidate,
  });
  const toggleExternal = useMutation({
    mutationFn: (allowExternalSources: boolean) =>
      api.patch(`/areas/${area.id}/profile`, { allowExternalSources }),
    onSuccess: invalidate,
  });
  const promote = useMutation({
    mutationFn: (id: string) => api.post(`/areas/${area.id}/discovered-sources/${id}/promote`, {}),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/areas/${area.id}/discovered-sources/${id}/dismiss`, {}),
    onSuccess: invalidate,
  });

  const canAdd = form.label.trim().length > 0 && form.url.trim().length > 0;

  return (
    <GlassPanel>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-ink text-base">Kilder Nova overvåker</h2>
        <span className="text-ink-soft text-[11px]">Omverdensovervåkeren følger disse</span>
      </div>

      <label className="border-hairline bg-base/30 mt-3 flex items-start gap-3 rounded-lg border px-3 py-2.5">
        <input
          type="checkbox"
          checked={area.allowExternalSources}
          disabled={!canEdit || toggleExternal.isPending}
          onChange={(e) => toggleExternal.mutate(e.target.checked)}
          className="mt-0.5"
        />
        <span className="min-w-0">
          <span className="text-ink block text-sm">La Nova søke utenfor lista</span>
          <span className="text-ink-soft block text-[11px]">
            På: Nova kan finne nye kilder selv og foreslår dem under. Av: kun kildene i lista
            overvåkes.
          </span>
        </span>
      </label>

      <div className="mt-3 space-y-2">
        {sources.map((s) => (
          <div
            key={s.id}
            className="border-hairline bg-base/30 flex items-start gap-2 rounded-lg border px-3 py-2"
          >
            <Globe size={13} className="text-ink-soft mt-0.5 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-ink inline-flex items-center gap-1 text-sm"
              >
                {s.label} <ExternalLink size={11} aria-hidden />
              </a>
              {s.notes && <span className="text-ink-soft block text-[11px]">{s.notes}</span>}
            </span>
            {canEdit && (
              <button
                type="button"
                aria-label={`Fjern ${s.label}`}
                onClick={() => remove.mutate(s.id)}
                className="text-ink-soft hover:text-danger shrink-0 transition"
              >
                <Trash2 size={13} aria-hidden />
              </button>
            )}
          </div>
        ))}
        {sources.length === 0 && (
          <p className="text-ink-soft text-xs">Ingen kilder lagt til ennå.</p>
        )}
      </div>

      {canEdit && (
        <div className="border-hairline mt-3 space-y-2 border-t pt-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Navn (f.eks. Digdir)"
              className={inputClass}
            />
            <input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
              className={inputClass}
            />
          </div>
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Hva skal Nova se etter her? (valgfritt)"
            className={inputClass}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} aria-hidden />}
              disabled={!canAdd}
              loading={add.isPending}
              onClick={() => add.mutate()}
            >
              Legg til kilde
            </Button>
            {add.error && <span className="text-danger text-xs">{(add.error as Error).message}</span>}
          </div>
        </div>
      )}

      {canEdit && discovered.length > 0 && (
        <div className="border-hairline mt-4 border-t pt-3">
          <p className="label-micro mb-2">Kilder Nova har funnet selv</p>
          <div className="space-y-2">
            {discovered.map((d) => (
              <div
                key={d.id}
                className="border-hairline bg-base/30 flex items-center gap-2 rounded-lg border px-3 py-2"
              >
                <span className="min-w-0 flex-1">
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-ink inline-flex items-center gap-1 truncate text-sm"
                  >
                    {d.label ?? d.url} <ExternalLink size={11} aria-hidden />
                  </a>
                  <span className="text-ink-soft block text-[11px]">Brukt {d.timesUsed} ganger</span>
                </span>
                <Button size="sm" loading={promote.isPending} onClick={() => promote.mutate(d.id)}>
                  Legg til
                </Button>
                <button
                  type="button"
                  aria-label="Avvis kilden"
                  onClick={() => dismiss.mutate(d.id)}
                  className="text-ink-soft hover:text-danger shrink-0 transition"
                >
                  <X size={14} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </GlassPanel>
  );
}

/** The descriptive copy that shapes what Nova writes for the area. */
function AreaProfileForm({ area }: { area: AreaDto }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    description: area.description ?? '',
    valueProposition: area.valueProposition ?? '',
    targetAudience: area.targetAudience ?? '',
  });
  const [saved, setSaved] = useState(false);

  const dirty =
    form.description !== (area.description ?? '') ||
    form.valueProposition !== (area.valueProposition ?? '') ||
    form.targetAudience !== (area.targetAudience ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<AreaDto>(`/areas/${area.id}/profile`, {
        description: form.description || null,
        valueProposition: form.valueProposition || null,
        targetAudience: form.targetAudience || null,
      }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['areas'] });
    },
  });

  return (
    <div className="mt-4 space-y-4">
      <ProfileField
        label="Beskrivelse"
        hint="Hva området er, kort fortalt."
        value={form.description}
        onChange={(description) => setForm({ ...form, description })}
        rows={3}
      />
      <ProfileField
        label="Verdiløfte"
        hint="Hva vi lover kundene på dette området."
        value={form.valueProposition}
        onChange={(valueProposition) => setForm({ ...form, valueProposition })}
        rows={2}
      />
      <ProfileField
        label="Målgruppe"
        hint="Hvem vi snakker til."
        value={form.targetAudience}
        onChange={(targetAudience) => setForm({ ...form, targetAudience })}
        rows={2}
      />

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          size="sm"
          icon={<Save size={14} aria-hidden />}
          disabled={!dirty}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Lagre detaljer
        </Button>
        {saved && (
          <span className="text-success inline-flex items-center gap-1 text-xs">
            <Check size={13} aria-hidden />
            Lagret
          </span>
        )}
        {save.error && <span className="text-danger text-xs">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function ProfileField({
  label,
  hint,
  value,
  onChange,
  rows,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
}) {
  const id = `area-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <label className="label-micro mb-1.5 block" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        spellCheck
        lang="nb"
        placeholder={hint}
        className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-y rounded-lg border p-3 text-xs leading-relaxed focus:outline-none"
      />
    </div>
  );
}

/** Who works in the area. Adding and removing is the owner's (and admin's) call. */
function CoworkersPanel({ area, canManage }: { area: AreaDto; canManage: boolean }) {
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsers();

  const coworkers = (area.members ?? []).filter((m) => m.role === 'MEMBER');
  // The owner and anyone already involved (co-worker or on the reminder list).
  const takenIds = new Set([
    area.ownerUserId,
    ...(area.members ?? []).map((m) => m.userId),
  ]);
  const addable = users.filter((u: UserDto) => u.isActive && !takenIds.has(u.id));

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['areas'] });

  const add = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/areas/${area.id}/members`, { userId, role: 'MEMBER' }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.delete(`/areas/${area.id}/members/${userId}`),
    onSuccess: invalidate,
  });

  return (
    <GlassPanel>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-ink text-base">Medarbeidere</h2>
        <span className="text-ink-soft text-[11px]">
          Jobber i området — varsles om kommentarer, ikke purringer
        </span>
      </div>

      <div className="mt-3 space-y-2">
        <div className="border-hairline bg-base/30 flex items-center gap-2 rounded-lg border px-3 py-2">
          <span className="bg-accent/15 text-accent flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold">
            {(area.owner?.name ?? '?').slice(0, 1)}
          </span>
          <span className="text-ink text-sm">{area.owner?.name ?? 'Ukjent'}</span>
          <span className="text-ink-soft ml-auto text-[11px]">Ansvarlig</span>
        </div>

        {coworkers.map((m) => (
          <div
            key={m.id}
            className="border-hairline bg-base/30 flex items-center gap-2 rounded-lg border px-3 py-2"
          >
            <span className="bg-raised text-ink-soft flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold">
              {(m.user?.name ?? '?').slice(0, 1)}
            </span>
            <span className="text-ink text-sm">{m.user?.name ?? 'Ukjent'}</span>
            {canManage && (
              <button
                type="button"
                aria-label={`Fjern ${m.user?.name ?? 'medarbeider'}`}
                onClick={() => remove.mutate(m.userId)}
                className="text-ink-soft hover:text-danger ml-auto transition"
              >
                <X size={14} aria-hidden />
              </button>
            )}
          </div>
        ))}

        {coworkers.length === 0 && (
          <p className="text-ink-soft text-xs">Ingen medarbeidere lagt til ennå.</p>
        )}
      </div>

      {canManage && addable.length > 0 && (
        <div className="relative mt-3">
          <UserPlus
            size={13}
            className="text-ink-soft pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            aria-hidden
          />
          <select
            aria-label="Legg til medarbeider"
            value=""
            disabled={add.isPending}
            onChange={(e) => e.target.value && add.mutate(e.target.value)}
            className="border-hairline bg-base/40 text-ink-soft h-9 w-full rounded-lg border pl-8 pr-3 text-xs"
          >
            <option value="">Legg til medarbeider …</option>
            {addable.map((u: UserDto) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {(add.error || remove.error) && (
        <p className="text-danger mt-2 text-xs">
          {((add.error ?? remove.error) as Error).message}
        </p>
      )}
    </GlassPanel>
  );
}
